import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Alert, Button, Accordion, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCloud, 
  faLink, 
  faSignOutAlt, 
  faExclamationTriangle, 
  faHome, 
  faChevronRight,
  faSync,
  faToggleOn,
  faBars
} from '@fortawesome/free-solid-svg-icons';
import './PilotDashboard.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import djiBridge from '../utils/cloud-sample-DjiBridge';
import cloudModule, { EComponentName, EStatusValue } from '../utils/cloud-sample-cloudModule';
import { ELocalStorageKey, API_ENDPOINTS } from '../utils/constants';

/* eslint-disable no-undef */

// Configuration constants - needed for WebSocket URL
const CURRENT_CONFIG = {
  // Configuration for the Pilot dashboard that can be dynamically updated
  // from localStorage to avoid hardcoded values
  token: localStorage.getItem(ELocalStorageKey.Token),
  username: localStorage.getItem(ELocalStorageKey.Username),
  userId: localStorage.getItem(ELocalStorageKey.UserId),
  websocketURL: localStorage.getItem(ELocalStorageKey.WsUrl)
};

// API endpoints using relative paths through nginx proxy
const USER_INFO_URL = API_ENDPOINTS.USER_INFO;
const PLATFORM_INFO_URL = `/api/workspace/info`;
const DEVICE_INFO_URL = (workspaceId, deviceSn) => `/api/workspace/${workspaceId}/devices/${deviceSn}`;
const BIND_DEVICE_URL = (deviceSn) => `/api/devices/${deviceSn}/binding`;

/**
 * PilotDashboard Component
 */
const PilotDashboard = () => {
  const navigate = useNavigate();
  
  // Define localStorage keys exactly as in pilot-home.vue - MOVED TO TOP
  const ELocalStorageKey = {
    Token: 'x-auth-token',
    Username: 'username',
    UserId: 'user_id',
    WorkspaceId: 'workspace_id',
    WorkspaceName: 'workspace_name',
    WorkspaceDesc: 'workspace_desc',
    PlatformName: 'platform_name',
    GatewayOnline: 'gatewayOnline',
    Device: 'device',
    Flag: 'flag',
    WebsdkNgInstallId: 'websdk_ng_install_id',
    WsUrl: 'wsUrl',
    MqttConfig: 'mqttConfig'
  };
  
  const [username, setUsername] = useState(localStorage.getItem(ELocalStorageKey.Username) || '');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [exitVisible, setExitVisible] = useState(false);
  const [workspaceName, setWorkspaceName] = useState(localStorage.getItem(ELocalStorageKey.WorkspaceName) || '');
  const [wsId, setWsId] = useState(localStorage.getItem(ELocalStorageKey.WorkspaceId) || '');
  
  // Connection states - exactly as in pilot-home.vue
  const [thingState, setThingState] = useState(EStatusValue.DISCONNECT);
  const [apiState, setApiState] = useState(EStatusValue.DISCONNECT);
  const [wsState, setWsState] = useState(EStatusValue.DISCONNECT);
  const [liveState, setLiveState] = useState(EStatusValue.DISCONNECT);
  const [mapState, setMapState] = useState(EStatusValue.DISCONNECT);
  const [tsaState, setTsaState] = useState(EStatusValue.DISCONNECT);
  const [mediaState, setMediaState] = useState(EStatusValue.DISCONNECT);
  const [waylineState, setWaylineState] = useState(EStatusValue.DISCONNECT);
  
  // Components map to match pilot-home.vue's components handling
  const [components, setComponents] = useState(new Map());
  
  // Device information - exactly matching structure in pilot-home.vue
  const [device, setDevice] = useState({
    data: {
      sn: '',
      online_status: false,
      device_callsign: '',
      user_id: '',
      user_callsign: '',
      bound_status: false,
      model: '',
      gateway_sn: EStatusValue.DISCONNECT,
      domain: -1
    }
  });
  
  // Binding parameters - exactly matching structure in pilot-home.vue
  const [bindParam, setBindParam] = useState({
    device_sn: '',
    user_id: '',
    workspace_id: wsId
  });
  
  // Module list for drawer - exactly matching structure in pilot-home.vue
  const modules = [
    { name: 'Cloud', state: thingState, module: EComponentName.Thing, enabled: true },
    { name: 'Api', state: apiState, module: EComponentName.Api, enabled: true },
    { name: 'Ws', state: wsState, module: EComponentName.Ws, enabled: true },
    // Auto-loaded modules
    { name: 'Map', state: mapState, module: EComponentName.Map, enabled: true },
    { name: 'Tsa', state: tsaState, module: EComponentName.Tsa, enabled: true },
    // Disabled modules (not auto-loaded)
    { name: 'Live', state: liveState, module: EComponentName.Liveshare, enabled: false },
    { name: 'Media', state: mediaState, module: EComponentName.Media, enabled: true },
    { name: 'Wayline', state: waylineState, module: EComponentName.Mission, enabled: true }
  ];
  
  /**
   * Handle API error responses, particularly 401 Unauthorized
   * @param {Error} error - The error from an API call
   */
  const handleApiError = async (error, navigateFunc) => {
    console.error('[PILOT] API error:', error);
    console.log('[PILOT] Error response:', error.response ? error.response.data : 'No response data');
    
    // If unauthorized (401), token is expired or invalid - redirect to login
    if (error.response && error.response.status === 401) {
      console.log('[PILOT] Received 401 Unauthorized, redirecting to login');
      
      // IMPORTANT: Only clear token from localStorage, not from DJI Pilot
      // We should never call apiSetToken with empty string as it could break the module
      localStorage.removeItem(ELocalStorageKey.Token);
      console.log('[PILOT] Cleared invalid token from localStorage only');
      
      // Redirect to login - token refresh will be handled by PilotLogin
      navigateFunc('/login');
    }
    
    return error;
  };
  
  // Initialize dashboard when component mounts
  // Following exact sequence from pilot-home.vue onMounted function
  useEffect(() => {
    console.log('[PILOT] Initializing dashboard');
    
    // Register callbacks and back click handler exactly as in pilot-home.vue
    djiBridge.onBackClickReg();
    djiBridge.onStopPlatform();
    
    // Define global callbacks for DJI bridge
    window.connectCallback = (arg) => {
      connectCallback(arg);
    };
    window.wsConnectCallback = (arg) => {
      wsConnectCallback(arg);
    };
    
    // Load device data from localStorage if available
    const oldDevice = localStorage.getItem(ELocalStorageKey.Device);
    if (oldDevice) {
      try {
        setDevice({ data: JSON.parse(oldDevice) });
      } catch (err) {
        console.error('[PILOT] Error parsing device data:', err);
      }
    }
    
    // Get device info from bridge exactly as in pilot-home.vue
    const gatewaySn = djiBridge.getRemoteControllerSN();
    console.log('[PILOT] Got controller SN from bridge:', gatewaySn);
    
    if (gatewaySn === EStatusValue.DISCONNECT.toString()) {
      setError('Data is not available, please restart the remote control.');
      return;
    }
    
    // Store controller SN in localStorage for binding
    localStorage.setItem(ELocalStorageKey.ControllerSN, gatewaySn);
    console.log('[PILOT] Saved controller SN to localStorage:', gatewaySn);
    
    // Update device data with SN information
    setDevice(prev => ({
      data: {
        ...prev.data,
        gateway_sn: gatewaySn,
        sn: djiBridge.getAircraftSN()
      }
    }));
    
    // Update bind parameters with the gateway SN - do this once during initialization
    setBindParam(prev => ({
      ...prev,
      device_sn: gatewaySn
    }));
    
    // Get user info first -- this returns mqttConfig and workspace data
    getUserInfo()
      .then(res => {
        if (!res || !res.data) {
          setError('Failed to get user info');
          setLoading(false);
          return;
        }
        
        setUsername(res.data.username);
        localStorage.setItem(ELocalStorageKey.Username, res.data.username);
        
        const ws = res.data.workspace || res.data.mqttConfig || {};
        setWorkspaceName(ws.workspace_name || '');
        setWsId(ws.workspace_id || res.data.workspace_id || '');
        
        setBindParam(prev => ({
          ...prev,
          user_id: res.data.user_id,
          workspace_id: res.data.workspace_id
        }));
        
        // Set platform info BEFORE loading Thing -- DJI Bridge needs this context
        setWorkspaceInfo();
        
        // Now load Thing module -- mqttConfig is in localStorage from getUserInfo
        console.log('[PILOT] Loading Thing module after getUserInfo + platformSetInformation');
        try {
          const storedMqttConfig = localStorage.getItem(ELocalStorageKey.MqttConfig);
          if (storedMqttConfig) {
            const mqttConfig = JSON.parse(storedMqttConfig);
            console.log('[PILOT] Setting Thing component parameters:', {
              host: mqttConfig.brokerUrl || mqttConfig.host || `${mqttConfig.server}:${mqttConfig.port}`,
              username: mqttConfig.username
            });
            
            djiBridge.setComponentParam(EComponentName.Thing, {
              host: mqttConfig.brokerUrl || mqttConfig.host || `${mqttConfig.server}:${mqttConfig.port}`,
              username: mqttConfig.username,
              password: mqttConfig.password,
              connectCallback: 'connectCallback'
            });
            
            const thingLoaded = djiBridge.loadComponent(
              EComponentName.Thing, 
              djiBridge.getComponentParam(EComponentName.Thing)
            );
            console.log('[PILOT] Thing component load result:', thingLoaded);
          } else {
            console.error('[PILOT] No MQTT config found in localStorage after getUserInfo');
            setError('No MQTT connection information available');
          }
        } catch (err) {
          console.error('[PILOT] Error loading Thing component:', err);
          setError('Failed to load MQTT component');
        }
        
        setLoading(false);
        
        // Get device info after a delay
        setTimeout(getDeviceInfo, 3000);
      })
      .catch(err => {
        console.error('[PILOT] Error getting user info:', err);
        setError('Failed to get user info');
        setLoading(false);
      });
  }, []);

  /**
   * Get platform information from the server - exactly as in pilot-home.vue
   */
  const setWorkspaceInfo = async () => {
    // If workspace info already exists in localStorage, use it
    if (localStorage.getItem(ELocalStorageKey.WorkspaceName)) {
      djiBridge.platformSetInformation(
        localStorage.getItem(ELocalStorageKey.PlatformName) || '',
        localStorage.getItem(ELocalStorageKey.WorkspaceName) || '',
        localStorage.getItem(ELocalStorageKey.WorkspaceDesc) || ''
      );
      djiBridge.setWorkspaceId(wsId);
      return;
    }
    
    // Otherwise fetch from API
    try {
      const token = localStorage.getItem(ELocalStorageKey.Token);
      const response = await axios.get(PLATFORM_INFO_URL, {
        headers: { 'x-auth-token': token }
      });
      
      console.log('[PILOT] Platform info response:', response);
      
      if (response.data && response.data.code === 0 && response.data.data) {
        const platformData = response.data.data;
        setWorkspaceName(platformData.workspace_name);
        setWsId(platformData.workspace_id);
        
        localStorage.setItem(ELocalStorageKey.PlatformName, platformData.platform_name);
        localStorage.setItem(ELocalStorageKey.WorkspaceName, platformData.workspace_name);
        localStorage.setItem(ELocalStorageKey.WorkspaceDesc, platformData.workspace_desc);
        
        djiBridge.platformSetInformation(
          platformData.platform_name,
          platformData.workspace_name,
          platformData.workspace_desc
        );
        djiBridge.setWorkspaceId(platformData.workspace_id);
      }
    } catch (error) {
      console.error('[PILOT] Error fetching platform information:', error);
    }
  };
  
  /**
   * Get user information - exactly as in pilot-home.vue
   */
  const getUserInfo = async () => {
    try {
      const token = localStorage.getItem(ELocalStorageKey.Token);
      console.log('[PILOT] Getting user info with token:', token ? token.substring(0, 10) + '...' : 'null');
      console.log('[PILOT] USER_INFO_URL:', USER_INFO_URL);
      
      const headers = {};
      headers['x-auth-token'] = token;
      console.log('[PILOT] Request headers:', headers);
      
      const response = await axios.get(USER_INFO_URL, { headers });
      
      console.log('[PILOT] User info response:', response.data);
      
      // Store MQTT configuration if returned
      if (response.data && response.data.data && response.data.data.mqttConfig) {
        console.log('[PILOT] Received MQTT configuration from server:', {
          username: response.data.data.mqttConfig.username,
          server: response.data.data.mqttConfig.server || response.data.data.mqttConfig.host,
          port: response.data.data.mqttConfig.port
        });
        
        localStorage.setItem(ELocalStorageKey.MqttConfig, JSON.stringify(response.data.data.mqttConfig));
        
        if (response.data.data.mqttConfig.wsUrl) {
          localStorage.setItem(ELocalStorageKey.WsUrl, response.data.data.mqttConfig.wsUrl);
          console.log('[PILOT] Updated WebSocket URL:', response.data.data.mqttConfig.wsUrl);
        }
      }
      
      // Store workspace/platform info from the response
      const ws = response.data.data.workspace || response.data.data.mqttConfig || {};
      if (ws.workspace_name) localStorage.setItem(ELocalStorageKey.WorkspaceName, ws.workspace_name);
      if (ws.workspace_desc) localStorage.setItem(ELocalStorageKey.WorkspaceDesc, ws.workspace_desc);
      if (ws.platform_name) localStorage.setItem(ELocalStorageKey.PlatformName, ws.platform_name);
      if (ws.workspace_id) localStorage.setItem(ELocalStorageKey.WorkspaceId, ws.workspace_id);
      
      return response.data;
    } catch (error) {
      console.error('[PILOT] Error fetching user information:', error);
      if (error.response) {
        console.error('[PILOT] Response status:', error.response.status);
        console.error('[PILOT] Response data:', error.response.data);
      }
      throw error;
    }
  };
  
  /**
   * Get device information - exactly as in pilot-home.vue
   */
  const getDeviceInfo = async () => {
    if (!device.data.sn || device.data.sn === EStatusValue.DISCONNECT) {
      return;
    }
    
    try {
      const token = localStorage.getItem(ELocalStorageKey.Token);
      const response = await axios.get(DEVICE_INFO_URL(bindParam.workspace_id, device.data.sn), {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data.code !== 0) {
        return;
      }
      
      setDevice(prev => ({
        data: {
          ...prev.data,
          online_status: response.data.data.status,
          bound_status: response.data.data.bound_status,
          device_callsign: response.data.data.nickname,
          model: response.data.data.device_name
        }
      }));
      
      localStorage.setItem(ELocalStorageKey.Device, JSON.stringify(device.data));
    } catch (error) {
      console.error('[PILOT] Error fetching device information:', error);
    }
  };
  
  /**
   * Refresh status - exactly as in pilot-home.vue refreshStatus function
   */
  const refreshStatus = () => {
    setThingState(djiBridge.thingGetConnectState() ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setApiState(djiBridge.isComponentLoaded(EComponentName.Api) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setLiveState(djiBridge.isComponentLoaded(EComponentName.Liveshare) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setWsState(djiBridge.isComponentLoaded(EComponentName.Ws) && djiBridge.wsGetConnectState()
      ? EStatusValue.CONNECTED
      : EStatusValue.DISCONNECT);
    setMapState(djiBridge.isComponentLoaded(EComponentName.Map) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setTsaState(djiBridge.isComponentLoaded(EComponentName.Tsa) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setMediaState(djiBridge.isComponentLoaded(EComponentName.Media) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setWaylineState(djiBridge.isComponentLoaded(EComponentName.Mission) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
  };
  
  /**
   * Module installation - exactly as in pilot-home.vue moduleInstall function
   */
  const moduleInstall = (m) => {
    console.log(`[PILOT] Installing ${m.name} module`);
    
    // Skip installation of disabled modules
    if (m.enabled === false) {
      console.log(`[PILOT] Module ${m.name} is disabled in this version`);
      setError(`Module ${m.name} is disabled in this simplified version`);
      setTimeout(() => setError(null), 3000); // Clear error after 3 seconds
      return;
    }
    
    let param;
    switch (m.module) {
      case EComponentName.Thing:
        // Get MQTT configuration from localStorage
        try {
          const storedMqttConfig = localStorage.getItem(ELocalStorageKey.MqttConfig);
          if (storedMqttConfig) {
            const mqttConfig = JSON.parse(storedMqttConfig);
            param = {
              host: mqttConfig.host || `${mqttConfig.server}:${mqttConfig.port}`,
              username: mqttConfig.username,
              password: mqttConfig.password,
              connectCallback: 'connectCallback'
            };
            console.log('[PILOT] Using stored MQTT configuration with username:', mqttConfig.username);
          } else {
            param = djiBridge.thingGetConfigs();
            console.log('[PILOT] No stored MQTT config, using bridge configs');
          }
        } catch (error) {
          console.error('[PILOT] Error parsing stored MQTT config:', error);
          param = djiBridge.thingGetConfigs();
        }
        break;
      case EComponentName.Api: {
        param = {
          host: djiBridge.getHost(),
          token: djiBridge.getToken()
        };
        break;
      }
      case EComponentName.Map: {
        const mapParams = {
          userName: localStorage.getItem(ELocalStorageKey.Username) || '',
          elementPreName: 'PILOT'
        };
        djiBridge.setComponentParam(EComponentName.Map, mapParams);
        console.log(`[PILOT] Map module parameters set:`, mapParams);
        param = djiBridge.getComponentParam(EComponentName.Map);
        break;
      }
      case EComponentName.Ws: {
        const token = localStorage.getItem(ELocalStorageKey.Token);
        
        // Get MQTT configuration from localStorage
        const mqttConfigStr = localStorage.getItem(ELocalStorageKey.MqttConfig);
        if (!mqttConfigStr) {
          console.error('[PILOT] No MQTT configuration found in localStorage for WS module installation');
          return;
        }
        
        try {
          const mqttConfig = JSON.parse(mqttConfigStr);
          console.log('[PILOT] MQTT config from localStorage:', mqttConfig);
          
          // Use wsUrl from the MQTT configuration
          const wsUrl = mqttConfig.wsUrl;
          if (!wsUrl) {
            console.error('[PILOT] No WebSocket URL in MQTT configuration');
            return;
          }
          
          console.log('[PILOT] Using WebSocket URL:', wsUrl);
          
          // Set up WebSocket parameters
          // The native DJI Bridge does NOT support query params or path-based auth
          // It connects to the simple base URL and uses a fallback unauthenticated session
          const wsParams = {
            host: wsUrl,
            token: token,
            connectCallback: 'wsConnectCallback'
          };
          
          console.log('[PILOT] Loading WS component with parameters:', {
            host: wsParams.host,
            token: wsParams.token ? `${wsParams.token.substring(0, 10)}...` : 'null',
            connectCallback: wsParams.connectCallback
          });
          
          // First set the component parameters
          djiBridge.setComponentParam(EComponentName.Ws, wsParams);
          
          // Then load the component with the parameters we just set
          const wsResult = djiBridge.loadComponent(
            EComponentName.Ws, 
            djiBridge.getComponentParam(EComponentName.Ws)
          );
          console.log('[PILOT] WS component load result:', wsResult);
        } catch (error) {
          console.error('[PILOT] Error parsing MQTT config:', error);
        }
        break;
      }
      case EComponentName.Media: {
        // Configure Media module with auto-upload settings
        const mediaParams = {
          autoUploadPhoto: true,        // Auto upload photos: TRUE
          autoUploadPhotoType: 0,       // Upload type: 0 = original (not thumbnail)
          autoUploadVideo: false        // Auto upload videos: FALSE
        };
        console.log('[PILOT] Media module parameters for manual install:', mediaParams);
        djiBridge.setComponentParam(EComponentName.Media, mediaParams);
        param = djiBridge.getComponentParam(EComponentName.Media);
        break;
      }
      default:
        param = djiBridge.getComponentParam(m.module);
    }
    
    djiBridge.setComponentParam(m.module, param);
    console.log(`[PILOT] ${m.name} module parameters:`, param);
    
    djiBridge.loadComponent(m.module, djiBridge.getComponentParam(m.module));
    refreshStatus();
  };
  
  /**
   * Module uninstallation - exactly as in pilot-home.vue moduleUninstall function
   */
  const moduleUninstall = (m) => {
    console.log(`[PILOT] Uninstalling ${m.name} module`);
    djiBridge.unloadComponent(m.module);
    refreshStatus();
  };
  
  /**
   * MQTT connection callback - exactly as in pilot-home.vue connectCallback function
   */
  const connectCallback = async (arg) => {
    console.log('[PILOT] Connect callback invoked with arg:', arg);
    
    if (arg) {
      setThingState(EStatusValue.CONNECTED);
      
      // First make sure API component is loaded with valid token
      if (!djiBridge.isComponentLoaded(EComponentName.Api)) {
        console.log('[PILOT] API component not loaded, checking token and loading component');
        
        // Verify token and load API component
        const isTokenValid = await verifyTokenValidity(navigate);
        if (!isTokenValid) {
          console.error('[PILOT] Token validation failed during MQTT connect callback');
          // verifyTokenValidity handles the redirection to login if token is invalid
          return;
        }
        
        // If we reach here, token is valid and API component should be loaded
        console.log('[PILOT] Token is valid and API component is loaded');
      }
      
      // ONLY Load Necessary Components: Thing, API, and WebSocket
      
      // Comment out Liveshare component loading
      /*
      console.log('[PILOT] Loading Liveshare component');
      const liveshareResult = djiBridge.loadComponent(
        EComponentName.Liveshare, 
        djiBridge.getComponentParam(EComponentName.Liveshare)
      );
      console.log('[PILOT] Liveshare component load result:', liveshareResult);
      */
      
      // Set up and load WS component
      console.log('[PILOT] Setting WS component parameters');
      const token = localStorage.getItem(ELocalStorageKey.Token);
      
      // Get MQTT configuration from localStorage
      const mqttConfigStr = localStorage.getItem(ELocalStorageKey.MqttConfig);
      if (!mqttConfigStr) {
        console.error('[PILOT] No MQTT configuration found in localStorage');
        return;
      }
      
      try {
        const mqttConfig = JSON.parse(mqttConfigStr);
        console.log('[PILOT] MQTT config from localStorage:', mqttConfig);
        
        // Use wsUrl from the MQTT configuration
        const wsUrl = mqttConfig.wsUrl;
        if (!wsUrl) {
          console.error('[PILOT] No WebSocket URL in MQTT configuration');
          return;
        }
        
        console.log('[PILOT] Using WebSocket URL:', wsUrl);
        
        // Set up WebSocket parameters
        const wsParams = {
          host: wsUrl,
          token: token,
          connectCallback: 'wsConnectCallback'
        };
        
        console.log('[PILOT] Loading WS component with parameters:', {
          host: wsParams.host,
          token: wsParams.token ? `${wsParams.token.substring(0, 10)}...` : 'null',
          connectCallback: wsParams.connectCallback
        });
        
        // First set the component parameters
        djiBridge.setComponentParam(EComponentName.Ws, wsParams);
        
        // Then load the component with the parameters we just set
        const wsResult = djiBridge.loadComponent(
          EComponentName.Ws, 
          djiBridge.getComponentParam(EComponentName.Ws)
        );
        console.log('[PILOT] WS component load result:', wsResult);
      } catch (error) {
        console.error('[PILOT] Error parsing MQTT config:', error);
      }
      
      // Set up and load Map component - exactly as in cloud-sample reference
      console.log('[PILOT] Setting Map component parameters');
      const mapParams = {
        userName: username,
        elementPreName: 'PILOT'
      };
      djiBridge.setComponentParam(EComponentName.Map, mapParams);
      
      console.log('[PILOT] Loading Map component with parameters:', mapParams);
      const mapResult = djiBridge.loadComponent(
        EComponentName.Map, 
        djiBridge.getComponentParam(EComponentName.Map)
      );
      console.log('[PILOT] Map component load result:', mapResult);
      
      // Load TSA component - exactly as in cloud-sample reference
      console.log('[PILOT] Loading TSA component');
      const tsaResult = djiBridge.loadComponent(
        EComponentName.Tsa, 
        djiBridge.getComponentParam(EComponentName.Tsa)
      );
      console.log('[PILOT] TSA component load result:', tsaResult);
      
      try {
        console.log('[PILOT] 🚨 DEBUG: About to load Media module - if you see this, code path is correct');
        
        // ===================================================================
        // Media Module - Auto Upload Configuration
        // ===================================================================
        console.log('[PILOT] Loading Media component with auto-upload configuration');
      
      // Configure Media module parameters
      const mediaParams = {
        autoUploadPhoto: true,        // Auto upload photos: TRUE
        autoUploadPhotoType: 0,       // Upload type: 0 = original (not thumbnail)
        autoUploadVideo: false        // Auto upload videos: FALSE
      };
      
      console.log('[PILOT] Media configuration:', mediaParams);
      
      // Set the Media component parameters
      djiBridge.setComponentParam(EComponentName.Media, mediaParams);
      
      // Load the Media component
      const mediaResult = djiBridge.loadComponent(
        EComponentName.Media, 
        djiBridge.getComponentParam(EComponentName.Media)
      );
      console.log('[PILOT] Media component load result:', mediaResult);
      
      // Set download owner AFTER component loads
      if (mediaResult) {
        console.log('[PILOT] Setting download owner to 0 (Mine)');
        
        if (window.djiBridge && typeof window.djiBridge.mediaSetDownloadOwner === 'function') {
          const downloadOwnerResult = window.djiBridge.mediaSetDownloadOwner(0);  // 0 = Mine
          console.log('[PILOT] Set download owner result:', downloadOwnerResult);
        } else {
          console.warn('[PILOT] mediaSetDownloadOwner not available in native bridge');
        }
        
        console.log('[PILOT] Media module loaded and configured successfully');
      } else {
        console.error('[PILOT] Failed to load Media component');
      }
      } catch (mediaError) {
        console.error('[PILOT] ❌ EXCEPTION during Media loading:', mediaError);
        console.error('[PILOT] Error stack:', mediaError.stack);
      }
      
      // ===================================================================
      // Mission/Wayline Module - Auto Load
      // ===================================================================
      console.log('[PILOT] Loading Mission component');
      
      try {
        // Load the Mission component (no parameters needed for basic functionality)
        const missionResult = djiBridge.loadComponent(EComponentName.Mission, {});
        console.log('[PILOT] Mission component load result:', missionResult);
        
        if (missionResult) {
          console.log('[PILOT] Mission module loaded successfully');
        } else {
          console.error('[PILOT] Failed to load Mission component');
        }
      } catch (missionError) {
        console.error('[PILOT] ❌ EXCEPTION during Mission loading:', missionError);
        console.error('[PILOT] Error stack:', missionError.stack);
      }
      
      // Start device binding loop exactly as in pilot-home.vue
      console.log('[PILOT] Starting device binding loop with parameters:', {
        device_sn: bindParam.device_sn,
        user_id: bindParam.user_id ? bindParam.user_id.substring(0, 8) + '...' : 'null',
        workspace_id: bindParam.workspace_id ? bindParam.workspace_id.substring(0, 8) + '...' : 'null'
      });
      
      let bindInterval = setInterval(() => {
        // Get device SN from bindParam or fallback to localStorage
        const deviceSn = bindParam.device_sn || localStorage.getItem(ELocalStorageKey.ControllerSN);
        
        if (!deviceSn) {
          console.log('[PILOT] No device SN available (checked bindParam and localStorage), cannot bind');
          return;
        }
        
        // Get user_id and workspace_id from bindParam or localStorage
        const userId = bindParam.user_id || localStorage.getItem(ELocalStorageKey.UserId);
        const workspaceId = bindParam.workspace_id || localStorage.getItem(ELocalStorageKey.WorkspaceId);
        
        if (!userId || !workspaceId) {
          console.log('[PILOT] Binding parameters incomplete (user_id or workspace_id missing), skipping attempt');
          return;
        }
        
        const bindRequestData = {
          device_sn: deviceSn,
          user_id: userId,
          workspace_id: workspaceId
        };
        
        console.log('[PILOT] Attempting to bind device:', deviceSn);
        
        // Bind request using token from localStorage - matches pilot-home.vue
        const headers = {};
        headers['x-auth-token'] = localStorage.getItem(ELocalStorageKey.Token);
        
        axios.post(BIND_DEVICE_URL(deviceSn), bindRequestData, { headers })
          .then(bindRes => {
            console.log('[PILOT] Binding response:', bindRes.data);
            if (bindRes.data.code !== 0) {
              console.error('[PILOT] Binding failed:', bindRes.data.message);
            } else {
              console.log('[PILOT] ✅ Device bound successfully!');
              clearInterval(bindInterval);
            }
          }).catch(async err => {
            console.error('[PILOT] Binding request error:', err.message);
            if (err.response) {
              console.error('[PILOT] Error response:', err.response.data);
            }
            await handleApiError(err, navigate);
          });
      }, 2000);
      
      // Get device info after a delay
      setTimeout(getDeviceInfo, 3000);
    } else {
      setThingState(EStatusValue.DISCONNECT);
    }
    
    refreshStatus();
  };
  
  /**
   * WebSocket connection callback - exactly as in pilot-home.vue wsConnectCallback function
   */
  const wsConnectCallback = async (arg) => {
    setWsState(arg ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
  };
  
  /**
   * Show module status drawer - exactly as in pilot-home.vue showStatus function
   */
  const showStatus = () => {
    let statusInterval = setInterval(() => {
      refreshStatus();
      if (!drawerVisible) {
        clearInterval(statusInterval);
      }
    }, 2000);
    
    setDrawerVisible(true);
  };
  
  /**
   * Confirm exit - exactly as in pilot-home.vue confirmAgain function
   */
  const confirmExit = () => {
    setExitVisible(true);
  };
  
  /**
   * Cancel exit - exactly as in pilot-home.vue onBack function
   */
  const cancelExit = () => {
    setExitVisible(false);
  };
  
  /**
   * Handle exit - exactly as in pilot-home.vue onExit function
   */
  const handleExit = () => {
    localStorage.clear();
    djiBridge.stopwebview();
  };
  
  /**
   * Handle logout
   */
  const handleLogout = () => {
    localStorage.removeItem(ELocalStorageKey.Token);
    localStorage.removeItem(ELocalStorageKey.Username);
    navigate('/login');
  };
  
  /**
   * Verify if token is valid by making a request to the server
   * @returns {Promise<boolean>} Whether the token is valid
   */
  const verifyTokenValidity = async (navigateFunc) => {
    // Try to get token from localStorage first
    const token = localStorage.getItem(ELocalStorageKey.Token);
    
    // If no token is found, return false
    if (!token) {
      console.error('[PILOT] No token found in localStorage');
      return false;
    }
    
    try {
      console.log('[PILOT] Verifying token validity with backend request');
      
      // Make a request to the user info endpoint to check token validity
      const headers = {};
      headers[ELocalStorageKey.Token] = token;
      
      console.log('[PILOT] Making user info request with headers:', headers);
      console.log('[PILOT] Request URL:', USER_INFO_URL);
      
      const response = await axios.get(USER_INFO_URL, { headers });
      
      console.log('[PILOT] User info response:', response.data);
      
      // If response is success WITH CODE 0, token is valid
      if (response.status === 200 && response.data.code === 0) {
        console.log('[PILOT] Token is valid (code=0)');
        
        // Load API component if not already loaded
        if (!djiBridge.isComponentLoaded(EComponentName.Api)) {
          // Set API component parameters with token
          djiBridge.setComponentParam(EComponentName.Api, {
            host: window.location.origin,
            token: token
          });
          
          // Load API component
          const apiLoaded = djiBridge.loadComponent(
            EComponentName.Api,
            djiBridge.getComponentParam(EComponentName.Api)
          );
          
          if (!apiLoaded) {
            console.error('[PILOT] Failed to load API component');
            return false;
          }
          
          // Set token in DJI Pilot using apiSetToken
          if (token && token.trim() !== '') {
            console.log(`[PILOT] Setting token using djiBridge.setToken: ${token.substring(0, 10)}...`);
            const setResult = djiBridge.setToken(token);
            console.log('[PILOT] setToken result:', setResult);
          }
        }
        
        return true;
      } else {
        console.log('[PILOT] Token validation failed: Status OK but code not 0:', response.data.code);
        
        // Token is invalid, redirect to login
        localStorage.removeItem(ELocalStorageKey.Token);
        navigateFunc('/login');
        return false;
      }
    } catch (error) {
      console.error('[PILOT] Error validating token:', error);
      console.log('[PILOT] Error response:', error.response ? error.response.data : 'No response data');
      
      // If 401 unauthorized error, token is expired or invalid
      if (error.response && error.response.status === 401) {
        console.log('[PILOT] Token is invalid or expired (401)');
        localStorage.removeItem(ELocalStorageKey.Token);
        navigateFunc('/login');
      }
      
      return false;
    }
  };
  
  return (
    <Container fluid className="dashboard-container p-0" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header - exactly matching the image */}
      <Row className="dashboard-header align-items-center m-0" style={{ 
        backgroundColor: '#ffffff', 
        color: '#1a2332', 
        height: '60px',
        padding: '0 20px',
        fontSize: '15px',
        borderBottom: '1px solid #cfd8dc'
      }}>
        <Col xs={2} className="text-start p-0">
          {/* Back chevron removed */}
        </Col>
        <Col xs={8} className="text-center p-0">
          <h4 className="mb-0 fontBold" style={{ color: '#1a2332', fontWeight: 600, fontSize: '18px' }}>
            {localStorage.getItem(ELocalStorageKey.WorkspaceDesc) || ''}
          </h4>
        </Col>
        <Col xs={2} className="text-end d-flex justify-content-end p-0">
          <Button 
            variant="link" 
            className="p-0" 
            onClick={showStatus} 
            style={{ color: '#546e7a' }}
          >
            <FontAwesomeIcon icon={faBars} size="lg" />
          </Button>
        </Col>
      </Row>
      
      {/* Main content area - matching the image exactly */}
      <div style={{ 
        display: 'flex', 
        flex: 1, 
        backgroundColor: '#f5f7fa',
        overflow: 'auto',
        width: '100%' 
      }}>
        {/* Error alert - keep it but position properly */}
        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError(null)}
            style={{ position: 'absolute', top: '70px', left: '50%', transform: 'translateX(-50%)', zIndex: 100 }}
          >
            {error}
          </Alert>
        )}
        
        {/* Loading indicator */}
        {loading ? (
          <div className="text-center p-5 w-100">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Initializing dashboard...</p>
          </div>
        ) : (
          <>
            {/* Left column - white card with workspace info */}
            <div style={{ 
              width: '40%', 
              padding: '20px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{ 
                backgroundColor: '#ffffff', 
                borderRadius: '12px',
                border: '1px solid #eceff1',
                boxShadow: '0 1px 3px rgba(26,35,50,0.08)',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
              }}>
                <div style={{ padding: '20px' }}>
                  <div className="d-flex align-items-center">
                    <div>
                      <h5 style={{ 
                        fontSize: '18px', 
                        fontWeight: 600, 
                        marginBottom: '5px',
                        color: '#1a2332'
                      }}>
                        {workspaceName || 'Test Group One'}
                      </h5>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                          color: thingState === EStatusValue.CONNECTED && 
                                 apiState === EStatusValue.CONNECTED && 
                                 wsState === EStatusValue.CONNECTED ? 
                                 '#1565c0' : '#f57f17', 
                          marginRight: '5px' 
                        }}>
                          <FontAwesomeIcon 
                            icon={thingState === EStatusValue.CONNECTED && 
                                  apiState === EStatusValue.CONNECTED && 
                                  wsState === EStatusValue.CONNECTED ? 
                                  faLink : faExclamationTriangle} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div style={{
                  borderTop: '1px solid #cfd8dc',
                  marginTop: 'auto',
                  padding: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <Button 
                    variant="light" 
                    onClick={confirmExit}
                    style={{ 
                      minWidth: '150px',
                      backgroundColor: '#eceff1', 
                      border: 'none',
                      borderRadius: '8px',
                      color: '#c62828' 
                    }}
                  >
                    Exit
                  </Button>
                </div>
              </div>
            </div>

            {/* Right column - serial number and settings sections */}
            <div style={{ 
              width: '60%', 
              padding: '20px 20px 20px 0',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Serial Number Section */}
              <div style={{ marginBottom: '20px' }}>
                <h6 style={{ 
                  color: '#546e7a', 
                  marginBottom: '10px', 
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  paddingLeft: '10px'
                }}>
                  Serial Number
                </h6>
                <div style={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: '12px',
                  border: '1px solid #eceff1',
                  boxShadow: '0 1px 3px rgba(26,35,50,0.08)',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    padding: '15px 20px',
                    borderBottom: '1px solid #cfd8dc'
                  }}>
                    <span style={{ fontSize: '0.875rem', color: '#1a2332' }}>Remote Control Sn</span>
                    <span style={{ color: '#546e7a' }}>{device.data.gateway_sn || '—'}</span>
                  </div>
                  {device.data.online_status && device.data.sn && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '15px 20px' 
                    }}>
                      <span style={{ fontSize: '0.875rem', color: '#1a2332' }}>Aircraft Sn</span>
                      <span style={{ color: '#546e7a' }}>{device.data.sn}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Settings Section */}
              <div>
                <h6 style={{ 
                  color: '#546e7a', 
                  marginBottom: '10px', 
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  paddingLeft: '10px'
                }}>
                  Settings
                </h6>
                <div style={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: '12px',
                  border: '1px solid #eceff1',
                  boxShadow: '0 1px 3px rgba(26,35,50,0.08)',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    padding: '15px 20px',
                    borderBottom: '1px solid #cfd8dc'
                  }}>
                    <span style={{ fontSize: '0.875rem', color: '#1a2332' }}>Media File Upload</span>
                    <FontAwesomeIcon icon={faChevronRight} style={{ color: '#90a4ae' }} />
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    padding: '15px 20px',
                    borderBottom: '1px solid #cfd8dc'
                  }}>
                    <span style={{ fontSize: '0.875rem', color: '#1a2332' }}>Livestream Manually</span>
                    <FontAwesomeIcon icon={faChevronRight} style={{ color: '#90a4ae' }} />
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    padding: '15px 20px'
                  }}>
                    <span style={{ fontSize: '0.875rem', color: '#1a2332' }}>Open 3rd Party APP</span>
                    <FontAwesomeIcon icon={faChevronRight} style={{ color: '#90a4ae' }} />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Module Status Drawer - keep it exactly as it was */}
      <div className={`module-drawer ${drawerVisible ? 'show' : ''}`}>
        <div className="drawer-header">
          <h5>Module State</h5>
          <Button variant="link" onClick={() => setDrawerVisible(false)}>
            <FontAwesomeIcon icon={faSignOutAlt} />
          </Button>
        </div>
        <div className="drawer-body">
          {modules.map((m) => (
            <div key={m.name} className={`module-item ${m.enabled ? 'enabled' : 'disabled'}`}>
              <div className="module-name">{m.name} {!m.enabled && <span className="disabled-label">(Disabled)</span>}</div>
              <div className="module-actions">
                <Badge bg={m.state === EStatusValue.CONNECTED ? 'success' : 'danger'} className="me-2">
                  {m.state}
                </Badge>
                <Button variant="primary" size="sm" onClick={() => moduleInstall(m)} className="me-2" disabled={!m.enabled}>
                  Install
                </Button>
                <Button variant="danger" size="sm" onClick={() => moduleUninstall(m)} disabled={!m.enabled}>
                  Uninstall
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Exit Confirmation Modal */}
      <div className={`modal ${exitVisible ? 'show' : ''}`}>
        <div className="modal-content">
          <p>Data will not be synchronized between DJI Pilot and this server after exiting.</p>
          <div className="modal-footer">
            <Button variant="secondary" onClick={cancelExit}>Cancel</Button>
            <Button variant="danger" onClick={handleExit}>Exit</Button>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default PilotDashboard; 